// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import type { Node, Edge, NodeProps } from 'reactflow';
import dagre from '@dagrejs/dagre';
import 'reactflow/dist/style.css';
import { Button } from '../../components/common';
import { useDigitalTwin, TELEMETRY_POLL_INTERVAL_MS } from '../../hooks/useDigitalTwin';
import {
  cleanEntityType,
  getEntityDisplayType,
  getEntityIcon,
  categorizeStatus,
  extractMeasurements,
  isAssetEntityType,
  getTelemetryEntityIds,
  STATUS_COLORS,
  aggregateTelemetryState,
  aggregateChildStatus,
} from '../../utils/entityHelpers';
import type { StatusCategory, MeasurementEntry } from '../../utils/entityHelpers';
import type { Entity, Relationship } from '../../types/digitalTwin';
import './KnowledgeGraph.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EquipmentNodeData {
  label: string;
  equipmentType: string;
  status: StatusCategory;
  measurements: MeasurementEntry[];
  updatedAt?: string;
  /** Primary measurement to show on the node (e.g. "7,840 KiloW") */
  primaryMeasurement?: string;
}

// ---------------------------------------------------------------------------
// Dagre auto-layout
// ---------------------------------------------------------------------------

const NODE_WIDTH = 180;
const NODE_HEIGHT = 72;

/** Pick the most relevant measurement to show on a node. */
function pickPrimaryMeasurement(measurements: MeasurementEntry[]): string | undefined {
  // Prefer ActivePower, then SoC, then Voltage, then first available
  const priority = ['Active Power', 'So C', 'Voltage', 'Loading Percentage', 'Temperature'];
  for (const label of priority) {
    const m = measurements.find(e => e.key === label);
    if (m) return m.display;
  }
  return measurements[0]?.display;
}

function computeLayout(
  allEntities: Entity[],
  relationships: Relationship[]
): { nodes: Node<EquipmentNodeData>[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 80, ranksep: 100, edgesep: 40 });

  // Only include asset entities (exclude Site, telemetry points, documents, etc.).
  // Telemetry points are excluded structurally (via hasTelemetryPoint/hasPoint
  // edges) in addition to the type-name allow-list, so a telemetry entity
  // never renders as a standalone node even if its type isn't yet covered by
  // ASSET_ENTITY_TYPES.
  const telemetryIds = getTelemetryEntityIds(relationships);
  const nodeEntities = allEntities.filter(
    e => isAssetEntityType(e.type) && cleanEntityType(e.type) !== 'Site' && !telemetryIds.has(e.id)
  );

  for (const entity of nodeEntities) {
    g.setNode(entity.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }

  for (const rel of relationships) {
    if (g.hasNode(rel.source) && g.hasNode(rel.target)) {
      g.setEdge(rel.source, rel.target);
    }
  }

  dagre.layout(g);

  // Build entity map once to avoid O(N²) lookups in aggregation helpers
  const entityMap = new Map(allEntities.map(e => [e.id, e]));

  const nodes: Node<EquipmentNodeData>[] = nodeEntities.map(entity => {
    const nodeWithPosition = g.node(entity.id);
    const aggregatedState = aggregateTelemetryState(entity, allEntities, relationships, entityMap);
    const statusValue = aggregateChildStatus(entity, allEntities, relationships, entityMap);
    const measurements = extractMeasurements(aggregatedState);
    return {
      id: entity.id,
      type: 'equipment',
      position: {
        x: (nodeWithPosition?.x ?? 0) - NODE_WIDTH / 2,
        y: (nodeWithPosition?.y ?? 0) - NODE_HEIGHT / 2,
      },
      data: {
        label: entity.attributes?.label ?? entity.id,
        equipmentType: getEntityDisplayType(entity.type),
        status: categorizeStatus(statusValue),
        measurements,
        updatedAt: (aggregatedState?.updatedAt ?? entity.state?.updatedAt) as string | undefined,
        primaryMeasurement: pickPrimaryMeasurement(measurements),
      },
    };
  });

  const edges: Edge[] = relationships
    .filter(r => g.hasNode(r.source) && g.hasNode(r.target))
    .map(rel => ({
      id: rel.id,
      source: rel.source,
      target: rel.target,
      label: rel.type,
      type: 'smoothstep',
      style: { stroke: '#718096' },
    }));

  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Custom Node Component
// ---------------------------------------------------------------------------

function EquipmentNode({ data }: NodeProps<EquipmentNodeData>) {
  return (
    <div
      className="equipment-node"
      style={{ borderColor: STATUS_COLORS[data.status] || STATUS_COLORS.unknown }}
    >
      <Handle type="target" position={Position.Top} />
      <Handle type="target" position={Position.Left} />

      <div className="node-icon">{getEntityIcon(data.equipmentType)}</div>
      <div className="node-content">
        <div className="node-label">{data.label}</div>
        <div className="node-type">{data.equipmentType}</div>
        {data.primaryMeasurement && (
          <div className="node-measurement">{data.primaryMeasurement}</div>
        )}
      </div>
      <div
        className="node-status"
        style={{ backgroundColor: STATUS_COLORS[data.status] || STATUS_COLORS.unknown }}
      />

      <Handle type="source" position={Position.Bottom} />
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

const nodeTypes = {
  equipment: EquipmentNode,
};

// ---------------------------------------------------------------------------
// Available equipment palette for edit mode
// ---------------------------------------------------------------------------

const availableEquipment = [
  { type: 'UPS', icon: '⚡', label: 'UPS' },
  { type: 'PDU', icon: '🔌', label: 'PDU' },
  { type: 'CRAC', icon: '❄️', label: 'CRAC Unit' },
  { type: 'Chiller', icon: '🧊', label: 'Chiller' },
  { type: 'BESS', icon: '🔋', label: 'BESS' },
  { type: 'Server', icon: '🖥️', label: 'Server' },
  { type: 'Generator', icon: '⚙️', label: 'Generator' },
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

function KnowledgeGraph() {
  const { site_id } = useParams();
  const { entities, relationships, loading, error } = useDigitalTwin(site_id, {
    pollIntervalMs: TELEMETRY_POLL_INTERVAL_MS,
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node<EquipmentNodeData> | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);

  // Compute layout from API data
  const { nodes: computedNodes, edges: computedEdges } = useMemo(() => {
    if (entities.length === 0) return { nodes: [], edges: [] };
    return computeLayout(entities, relationships);
  }, [entities, relationships]);

  const [nodes, setNodes, onNodesChange] = useNodesState(computedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(computedEdges);

  // Re-sync when API data changes
  useEffect(() => {
    setNodes(computedNodes);
    setEdges(computedEdges);
  }, [computedNodes, computedEdges, setNodes, setEdges]);

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node<EquipmentNodeData>) => {
    setSelectedNode(node);
    setShowSidebar(true);
  }, []);

  const onPaneClick = useCallback(() => {
    setShowSidebar(false);
    setSelectedNode(null);
  }, []);

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
  };

  const handleRequestApproval = () => {
    // TODO: API call to submit changes for approval
    console.log('Requesting approval for knowledge graph changes');
    alert('Knowledge graph changes submitted for approval!');
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setNodes(computedNodes);
    setEdges(computedEdges);
    setIsEditMode(false);
  };

  if (loading) {
    return (
      <div className="knowledge-graph-container">
        <div className="kg-loading">Loading knowledge graph...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="knowledge-graph-container">
        <div className="kg-loading">
          <p>Failed to load knowledge graph</p>
          <p style={{ fontSize: '13px', color: '#b42318', marginTop: '8px' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="knowledge-graph-container">
      {/* Edit Mode Sidebar */}
      {isEditMode && (
        <div className="kg-sidebar">
          <div className="sidebar-header">
            <h3>Add Equipment</h3>
            <p>Drag items onto the canvas</p>
          </div>
          <div className="equipment-palette">
            {availableEquipment.map(eq => (
              <div key={eq.type} className="palette-item" draggable>
                <span className="palette-icon">{eq.icon}</span>
                <span className="palette-label">{eq.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Graph Canvas */}
      <div className="kg-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={isEditMode ? onNodesChange : undefined}
          onEdgesChange={isEditMode ? onEdgesChange : undefined}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          nodesDraggable={isEditMode}
          nodesConnectable={isEditMode}
          elementsSelectable={true}
          defaultEdgeOptions={{ animated: true }}
          fitView
          minZoom={0.02}
        >
          <Background />
          <Controls />
          <MiniMap
            nodeColor={node => {
              const status = (node.data?.status as StatusCategory) || 'unknown';
              return STATUS_COLORS[status] || STATUS_COLORS.unknown;
            }}
          />

          {/* Top Panel with Edit Button */}
          <Panel position="top-right">
            {!isEditMode ? (
              <Button onClick={handleEditToggle}>Edit Graph</Button>
            ) : (
              <div className="edit-actions">
                <Button variant="secondary" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button onClick={handleRequestApproval}>Request Approval</Button>
              </div>
            )}
          </Panel>
        </ReactFlow>
      </div>

      {/* Details Panel (Right Side) */}
      {showSidebar && selectedNode && (
        <div className="kg-details-panel">
          <div className="details-header">
            <button className="close-btn" onClick={() => setShowSidebar(false)}>
              ×
            </button>
            <div className="details-title">
              <span className="details-icon">{getEntityIcon(selectedNode.data.equipmentType)}</span>
              <div>
                <h3>{selectedNode.data.label}</h3>
                <p>{selectedNode.data.equipmentType}</p>
              </div>
            </div>
            <div
              className="details-status"
              style={{
                backgroundColor: STATUS_COLORS[selectedNode.data.status] || STATUS_COLORS.unknown,
              }}
            >
              {selectedNode.data.status}
            </div>
          </div>

          {/* Entity Info */}
          <div className="details-section">
            <h4>Entity Info</h4>
            <div className="specs-grid">
              <div className="spec-item">
                <span className="spec-label">Type</span>
                <span className="spec-value">{selectedNode.data.equipmentType}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Entity ID</span>
                <span className="spec-value">{selectedNode.id}</span>
              </div>
            </div>
          </div>

          {/* Real-Time Telemetry */}
          <div className="details-section">
            <h4>Real-Time Telemetry</h4>
            {selectedNode.data.measurements && selectedNode.data.measurements.length > 0 ? (
              <div className="specs-grid">
                {selectedNode.data.measurements.map(m => (
                  <div key={m.key} className="spec-item">
                    <span className="spec-label">{m.key}</span>
                    <span className="spec-value">{m.display}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="telemetry-unavailable">
                <p>No measurements available</p>
              </div>
            )}
            {selectedNode.data.updatedAt && (
              <p style={{ fontSize: '12px', color: '#718096', marginTop: '8px' }}>
                Updated: {new Date(selectedNode.data.updatedAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default KnowledgeGraph;
